import { FunctionComponent, useEffect } from "react";
import { Button } from "@mantine/core";
import Link from "next/link";
import useUserDiscord from "@form/hooks/useUserDiscord";
import useVerifyCampaign from "@form/hooks/useVerifyCampaign";
import mixpanel from "mixpanel-browser";
import useAccount from "@/hooks/useAccount";
import { CloseIcon } from "@form/components/common/icons";
import { confirm } from "@form/services/modal";

import type { DiscordForm } from "@form/components/form/modules/discord";
import type { SubmitError } from "@form/hooks/useVerifyCampaign";

const DiscordVerify: FunctionComponent<{
  form: DiscordForm;
  available: boolean;
  needVerify: boolean;
  submitError?: SubmitError;
}> = ({ form, available, needVerify, submitError }) => {
  const { login, isLogin } = useAccount();
  const { discord, connect, connecting, authLink, disconnect } =
    useUserDiscord();
  const { changeVerifyState, result } = useVerifyCampaign(`discord`);

  const isConnected = isLogin && !!discord && available;

  const showConnectError = needVerify && !result.discord;

  useEffect(() => {
    changeVerifyState(isConnected);
  }, [isConnected]);

  let verifyCount = 0;

  if (form.values.needFollowChannel && form.values.channel) {
    verifyCount++;
  }
  if (form.values.needJoinServer && form.values.serverLinks) {
    verifyCount += form.values.serverLinks.length;
  }

  return (
    <div className={available ? "" : "relative opacity-40 disable"}>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex items-center">
            {form.module.icon && (
              <form.module.icon className="w-5 h-5"></form.module.icon>
            )}
            <span className="text-body text-dark1 font-bold ml-2 dark:text-gray_f9">
              Discord
            </span>
            {showConnectError && !discord && !isConnected && (
              <span className="text-error text-sm ml-2">This is required</span>
            )}
            {discord && isConnected && (
              <div
                className="px-2 py-1 flex items-center rounded-[35px] text-bodyS text-gray_58 ml-2 dark:text-[#DCECFA]"
                style={{
                  boxShadow: "2px 4px 16px rgba(0, 0, 0, 0.05)",
                }}
              >
                <span>@{discord?.global_name || discord.username}</span>
                <div
                  className="action ml-1 w-[14px] h-[14px] rounded-full bg-primary flex items-center justify-center"
                  onClick={() => {
                    confirm({
                      title: "Are you sure to disconnect your account?",
                      okText: "Disconnect",
                      okType: "danger",
                      onOk() {
                        disconnect();
                      },
                    });
                  }}
                >
                  <CloseIcon className="w-[6px]"></CloseIcon>
                </div>
              </div>
            )}
            <div className="flex-1"></div>
            {isConnected && (
              <span className="text-green text-bodyS font-bold dark:text-dark_green">
                Connected
              </span>
            )}
          </div>
        </div>
        {!isConnected && (
          <div className="flex-shrink-0 ml-4">
            <a
              href={authLink}
              rel="noreferrer"
              className="block"
              target="_blank"
              onClick={(e) => {
                if (!isLogin) {
                  login();
                  e.preventDefault();
                  return false;
                }
                if (!authLink) {
                  e.preventDefault();
                  return false;
                }
                mixpanel.track("connect_discord");
                connect();
              }}
            >
              <Button
                loading={connecting}
                variant="outline"
                radius="xl"
                size="md"
              >
                Connect
              </Button>
            </a>
          </div>
        )}
      </div>
      <div
        className={`mt-2 ${isConnected ? "" : "hidden"} ${
          verifyCount > 1 ? "grid grid-cols-2 gap-2 " : ""
        }`}
      >
        {form.values.needJoinServer &&
          form.values.serverLinks &&
          form.values.serverLinks.map((serverLink, index) => {
            const joinError =
              submitError?.[form.values.uid]?.needJoinServer || [];
            const showError = joinError.includes(serverLink);
            return (
              <div key={index}>
                <div
                  className={`py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between dark:bg-[#192844] ${
                    showError ? "bg-error/10 border border-error" : ""
                  }`}
                >
                  <div className="flex items-center text-body text-dark1 flex-1 flex-wrap dark:text-gray_f9">
                    <span className="flex-shrink-0">Join this</span>
                    <Link passHref href={serverLink}>
                      <a
                        className="text-[#47679A] underline mx-1 flex-1 text-ellipsis overflow-hidden dark:text-[#87B5FF]"
                        target="_black"
                      >
                        server
                      </a>
                    </Link>
                  </div>
                  {/* <CheckIcon className={`flex-shrink-0 ml-2 ${accountVerified ? 'fill-green' : ''}`}></CheckIcon> */}
                </div>
                <div
                  className={`text-error text-sm mt-1 ${
                    showError ? "flex" : "hidden"
                  }`}
                >
                  This is required
                </div>
              </div>
            );
          })}
        {form.values.needFollowChannel && form.values.channel && (
          <div
            className={`py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between dark:bg-[#192844]`}
          >
            <div className="flex items-center text-body text-dark1 dark:text-gray_f9">
              Join this
              <Link passHref href={form.values.channel as string}>
                <a
                  className="text-[#47679A] underline mx-1 dark:text-[#87B5FF]"
                  target="_black"
                >
                  channel
                </a>
              </Link>
            </div>
            {/* <CheckIcon className={`${likeLinkVerified ? 'fill-green' : ''}`}></CheckIcon> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscordVerify;
